import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiSave, FiArrowLeft } from 'react-icons/fi';
import config from '../../config/config';
import { useLocation, useNavigate } from 'react-router-dom';

export function WeightAdd() {
  const location = useLocation();
  const navigate = useNavigate();

  // Destructuring passed state from WeightAdd main list
  const { category: passedCat, cat_name, subCat, type: passedType, day: passedDay } = location.state || {};

  const [weightData, setWeightData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchWeightReport = async () => {
    // Agar zaroori params missing hain toh fetch na karein
    if (!passedCat && !passedType) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const year = 2026;

      // Using passed props for specific batch data
      const queryParams = new URLSearchParams({
        year: year.toString(),
        category: passedCat || '',
        type: passedType || '',
        day: passedDay || ''
      }).toString();

      const response = await fetch(
        `${config.baseUrl}/api/reports/Weight-Print?${queryParams}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const data = await response.json();

      // FIXED: Check if data is array before mapping
      if (Array.isArray(data)) {
        const initializedData = data.map(item => ({
          ...item,
          wa: item.wa || '',
          wb: item.wb || '',
          wc: item.wc || '',
          wd: item.wd || '',
          wt: item.wt || '0.00'
        }));
        setWeightData(initializedData);
      } else {
        setWeightData([]);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeightReport();
  }, [passedCat, passedType, passedDay]);

  const handleInputChange = (index, field, value) => {
    const newData = [...weightData];
    newData[index][field] = value;

    const wa = parseFloat(newData[index].wa) || 0;
    const wb = parseFloat(newData[index].wb) || 0;
    const wc = parseFloat(newData[index].wc) || 0;
    const wd = parseFloat(newData[index].wd) || 0;
    newData[index].wt = (wa + wb + wc + wd).toFixed(2);

    setWeightData(newData);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        category: passedCat,
        type: passedType,
        day: passedDay,
        year: 2026,
        weights: weightData // Yeh wahi array hai jo aapne fetch kiya tha
      };
      const response = await fetch(`${config.baseUrl}/api/reports/bulk-update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('Saved!', 'Weights updated successfully', 'success');
      }
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-8">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-3 bg-white rounded-2xl shadow-sm border border-slate-200 hover:bg-slate-100 transition-all"
          >
            <FiArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight flex items-center gap-3">
              Weight Entry
            </h1>
            <p className="text-md font-bold text-indigo-600 uppercase tracking-widest pt-2">
              {cat_name} | {subCat} | Day {passedDay}
            </p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 bg-emerald-600 text-white px-8 py-3 rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg ${saving ? 'opacity-50' : ''}`}
        >
          <FiSave /> {saving ? 'SAVING...' : 'SAVE RECORDS'}
        </button>
      </div>

      {/* Table Section */}
      <div className="max-w-7xl mx-auto bg-white rounded-[2rem] shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-indigo-900 text-white font-black text-[11px] uppercase tracking-[0.2em]">
                <th className="px-8 py-6">S.No</th>
                <th className="px-6 py-6">Animal Number</th>
                <th className="px-4 py-6 text-center">Weight A</th>
                <th className="px-4 py-6 text-center">Weight B</th>
                <th className="px-4 py-6 text-center">Weight C</th>
                <th className="px-4 py-6 text-center">Weight D</th>
                <th className="px-8 py-6 text-right">Total (KG)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-24 text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent mx-auto"></div>
                  </td>
                </tr>
              ) : weightData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-20 text-center text-slate-400 font-bold">No animals found for this batch.</td>
                </tr>
              ) : weightData.map((item, index) => (
                <tr key={index} className="hover:bg-slate-50/80 transition-colors">
                  <td className="px-8 py-4 text-sm font-black text-slate-300">#{String(index + 1).padStart(2, '0')}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-base font-black text-slate-700">{item.animal_number}</span>
                  </td>

                  {['wa', 'wb', 'wc', 'wd'].map((field) => (
                    <td key={field} className="px-2 py-4">
                      <input
                        type="number"
                        value={item[field]}
                        placeholder="0.00"
                        onChange={(e) => handleInputChange(index, field, e.target.value)}
                        className="w-24 mx-auto block bg-slate-50 border-2 border-slate-100 focus:border-indigo-500 focus:bg-white text-center py-2.5 rounded-xl text-sm font-bold text-slate-800 outline-none transition-all"
                      />
                    </td>
                  ))}

                  <td className="px-8 py-4 text-right">
                    <span className={`text-lg font-black ${parseFloat(item.wt) > 0 ? 'text-indigo-700' : 'text-slate-300'}`}>
                      {item.wt || '0.00'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}